import { useEffect, useMemo, useRef, useState } from "react";
import { TbX } from "react-icons/tb";
import { CustomerAddress } from "data-types";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import RestrictedInputField from "../../../../components/CredentialsInput/RestrictedInputField";
import * as translator from "../../../../utils/translator";
import * as http from "../../../../utils/http";
import { validatePhone, validatePhoneDE } from "../../../../utils/fieldsValidator";
import { useAppContext } from "../../../../providers/AppProvider";
import { useShippingPageContext } from "../providers/ShippingPageProvider";

type AddressLike = {
  phone?: string | null;
  country_id?: string | null;
} | null | undefined;

/**
 * Per-country phone validation used by the invalid-phone popup and its submit guard.
 * DE → `validatePhoneDE`, everything else → `validatePhone`.
 * Same rule the address Zod schema and `AddNewAddressModal` use.
 */
export const validateAddressPhone = (
  phone: string,
  countryID: string | null | undefined,
  language: string,
) => {
  return countryID?.toLowerCase() === "de"
    ? validatePhoneDE(phone, language)
    : validatePhone(phone, language);
};

/** Returns true if the address' phone passes the per-country rule. */
export const isAddressPhoneValid = (address: AddressLike, language: string): boolean => {
  if (!address) return false;
  return validateAddressPhone(address.phone || "", address.country_id, language).isValid;
};

type Props = {
  isOpen: boolean;
  address: CustomerAddress | null;
  isMobile?: boolean;
  onClose: () => void;
  onSaved: (customerAddresses: { regular: any; packstation: any; all: any[] }, updatedAddressID: number) => void;
};

const InvalidPhonePopup: React.FC<Props> = ({ isOpen, address, isMobile = false, onClose, onSaved }) => {
  const { language } = useShippingPageContext();
  const { setStickyNotification } = useAppContext();
  const [phone, setPhone] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  // Remembers the last error message shown while the input had >= 10 digits, so we
  // can keep it on screen while the user is mid-typing with fewer digits instead of
  // flashing a less helpful "number has X digits" message.
  const lastErrorMsgRef = useRef<string>("");

  useEffect(() => {
    if (isOpen) {
      setPhone(address?.phone || "");
      setIsDirty(false);
      setIsSaving(false);
      lastErrorMsgRef.current = "";
    }
  }, [isOpen, address?.phone]);

  const validation = useMemo(() => {
    if (!address) return { isValid: false, errorMsg: "" };
    return validateAddressPhone(phone, address.country_id, language || "");
  }, [phone, address, language]);

  const isValid = validation.isValid;
  const digitCount = phone.replace(/\D/g, "").length;

  // Remember the latest real error while digits >= 10, so we can keep showing it
  // during mid-typing (< 10 digits) without flicker. Safe to update during render
  // because refs don't trigger re-renders.
  if (!isValid && digitCount >= 10) {
    lastErrorMsgRef.current = validation.errorMsg;
  }

  // Only show the inline error once the user has started editing, to match Figma
  // (empty/default state has no red message yet). While the input has < 10 digits
  // we keep the previously shown message to avoid mid-typing flicker.
  const inlineErrorMsg = !isDirty || isValid
    ? undefined
    : digitCount < 10
      ? (lastErrorMsgRef.current || undefined)
      : validation.errorMsg;

  const handleSave = () => {
    if (!address || !isValid || isSaving) return;

    setIsSaving(true);

    const payload: Record<string, any> = {
      address_id: address.id,
      first_name: address.first_name,
      last_name: address.last_name,
      zip: address.zip,
      city: address.city,
      address: address.address,
      company: address.company || null,
      address_sub_type: address.address_sub_type,
      country_id: address.country_id,
      phone,
    };

    http.postToServer("v2/customer-address", payload, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response: any) => {
        onSaved(response.data.customerAddresses, address.id);
        onClose();
      })
      .catch((error: any) => {
        if (error?.response?.status === 429) {
          setStickyNotification({
            show: true,
            title: translator.translate(language, "shipping", "AddNewAddressModal/rate_limit_error_title") || "Error!",
            content: translator.translate(language, "shipping", "AddNewAddressModal/rate_limit_error")
              || "Please wait 10 seconds before making another address change",
            level: "error",
            showInfoIcon: true,
            autoFade: true,
          });
        }
      })
      .finally(() => setIsSaving(false));
  };

  const title = translator.translate(language, "shipping", "InvalidPhonePopup/title") || "Invalid phone number";
  const subtitle = translator.translate(language, "shipping", "InvalidPhonePopup/subtitle") || "Please enter a valid phone number";
  const saveLabel = translator.translate(language, "shipping", "InvalidPhonePopup/save") || "Save number";
  const phonePlaceholder = translator.translate(language, "shipping", "ShippingForm/telephone") || "Phone Number";

  return (
    <PortaledModal
      shouldShowModal={isOpen}
      hideModal={onClose}
      shouldBackgroundClickHideModal={true}
    >
      <div
        id="invalid-phone-popup"
        className={`bg-white flex flex-col overflow-hidden ${
          isMobile
            ? "fixed bottom-0 left-0 right-0 rounded-t-[30px] pb-8 animate-fade-in-up"
            : "rounded-[20px] w-[min(400px,92vw)] pt-3 pb-4"
        }`}
      >
        <div className={`flex items-center justify-between shrink-0 ${isMobile ? "pl-4 pt-3" : "pl-3"}`}>
          <span
            id="invalid-phone-popup-title"
            className="text-[21px] font-semibold text-black font-poppins leading-[31.5px]"
          >
            {title}
          </span>
          <button
            id="invalid-phone-popup-close"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center w-11 h-11 border-none bg-white rounded-full cursor-pointer shrink-0"
          >
            <TbX size={20} color="#333" />
          </button>
        </div>

        <div className="flex flex-col gap-5 px-4 pt-3 pb-5">
          <p
            id="invalid-phone-popup-subtitle"
            className="text-base text-[#656d79] font-poppins leading-[19.5px] m-0"
          >
            {subtitle}
          </p>

          <RestrictedInputField
            id="invalid-phone-popup-input"
            ignoreTouched={true}
            value={phone}
            onChange={(value: string) => {
              setPhone(value);
              setIsDirty(true);
            }}
            placeholder={phonePlaceholder}
            mandatory={true}
            language={language}
            errorMsg={inlineErrorMsg}
          />
        </div>

        <div className="px-4 shrink-0">
          <button
            id="invalid-phone-popup-save"
            onClick={handleSave}
            disabled={!isValid || isSaving}
            className={`flex items-center justify-center gap-2 w-full py-5 px-8 rounded-xl border-none text-base font-medium font-poppins transition-colors duration-200 ${
              !isValid || isSaving
                ? "bg-[#eef0f4] text-[#98a0af] cursor-not-allowed"
                : "linear-background-color-primary text-white cursor-pointer"
            }`}
          >
            {isSaving
              ? (translator.translate(language, "shipping", "AddNewAddressModal/saving") || "Saving...")
              : saveLabel}
          </button>
        </div>
      </div>
    </PortaledModal>
  );
};

export default InvalidPhonePopup;
